import type { GlobalCrawlConfigInput } from "@repo/adapter-core";
import { z } from "zod";

export const DOMAIN_PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "apartment-unit-flat", label: "Apartment / Unit / Flat" },
  { value: "townhouse", label: "Townhouse" },
  { value: "villa", label: "Villa" },
  { value: "duplex", label: "Duplex" },
  { value: "semi-detached", label: "Semi-Detached" },
  { value: "terrace", label: "Terrace" },
  { value: "studio", label: "Studio" },
  { value: "block-of-units", label: "Block of Units" },
  { value: "acreage", label: "Acreage" },
  { value: "rural", label: "Rural" },
  { value: "land", label: "Land" },
  { value: "new-apartments", label: "New Apartments" },
  { value: "new-house-land", label: "New House & Land" },
] as const;

export const DOMAIN_LISTING_TYPES = [
  { value: "any", label: "Any (uses Domain default)" },
  { value: "buy", label: "For Sale" },
  { value: "rent", label: "For Rent" },
  { value: "sold", label: "Sold" },
] as const;

export const DOMAIN_TIME_RANGES = [
  { value: "last-7-days", label: "Last 7 days", days: "7" },
  { value: "last-30-days", label: "Last 30 days", days: "30" },
  { value: "last-90-days", label: "Last 90 days", days: "90" },
  { value: "last-year", label: "Last year", days: "365" },
  { value: "last-2-years", label: "Last 2 years", days: "730" },
  { value: "last-5-years", label: "Last 5 years", days: "1825" },
] as const;

export const DOMAIN_SORT_OPTIONS = [
  { value: "solddate-desc", label: "Sale Date (newest)", forTypes: ["sold"] },
  { value: "dateupdated-desc", label: "Date Updated", forTypes: ["buy", "rent"] },
  { value: "price-asc", label: "Price (low to high)", forTypes: ["buy", "sold", "rent"] },
  { value: "price-desc", label: "Price (high to low)", forTypes: ["buy", "sold", "rent"] },
] as const;

export const DomainAdapterConfigSchema = z.object({
  listingType: z.enum(["any", "buy", "rent", "sold"]).default("any"),
  propertyTypes: z.array(z.string()).optional(),
  minBedrooms: z.number().int().min(0).max(20).optional(),
  maxBedrooms: z.number().int().min(0).max(20).optional(),
  minBathrooms: z.number().int().min(0).max(10).optional(),
  minParking: z.number().int().min(0).max(10).optional(),
  minPrice: z.number().min(0).optional(),
  maxPrice: z.number().min(0).optional(),
  timeRange: z.string().optional(),
  // Computed by the orchestrator from absolute date ranges. Allows the URL
  // builder to map an arbitrary window to the closest Domain soldwithin
  // bucket without losing in-URL filtering.
  effectiveMaxAgeDays: z.number().int().min(0).optional(),
  dateFilterMode: z.enum(["relative", "absolute"]).default("relative"),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  sortBy: z.string().optional(),
  excludePriceWithheld: z.boolean().default(true),
  excludeSurroundingSuburbs: z.boolean().default(true),
  manualUrl: z.union([z.string().url(), z.literal("")]).optional(),
});

export type DomainAdapterConfig = z.infer<typeof DomainAdapterConfigSchema>;

export const DOMAIN_DEFAULT_CONFIG: GlobalCrawlConfigInput = {
  listingType: "any",
  dateFilterMode: "relative",
  excludePriceWithheld: true,
  excludeSurroundingSuburbs: true,
};

export function resolveDomainConfig(config: GlobalCrawlConfigInput = {}): DomainAdapterConfig {
  const listingType =
    config.listingType && config.listingType !== "any" ? config.listingType : "buy";

  return DomainAdapterConfigSchema.parse({
    ...DOMAIN_DEFAULT_CONFIG,
    ...config,
    listingType,
  });
}
